// /admin — gateway + shell for the operator surface (Block D).
// beforeLoad calls the backend's allowlist gateway and redirects
// non-admins back to /home (or /login when there's no session at all).
// The children render inside the shell's <Outlet />, which provides
// the sidebar nav (Beats / Fallbacks).
//
// Visual register is intentionally less atmospheric than the rest of
// the app — this is operations, not lore. Same color tokens, but
// flatter rhythm: no portrait, no presence verbs, no animations
// beyond the standard fadeUp.

import { Link, Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { getAdminMe } from '../lib/api';
import { authClient, getSessionSafe } from '../lib/auth-client';
import { redirectIfLandingOnly } from '../lib/landing-mode';

export const Route = createFileRoute('/admin')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
    const me = await getAdminMe();
    if (!me?.isAdmin) {
      // Authenticated but not in the allowlist — bounce them back to
      // the regular surface. /home will redirect to /awakening if they
      // don't have a bond yet.
      throw redirect({ to: '/home' });
    }
    return { adminEmail: me.email };
  },
  component: AdminShell,
});

function AdminShell() {
  const ctx = Route.useRouteContext();
  return (
    <main className="mx-auto max-w-5xl pt-12 sm:pt-8 lg:pt-4">
      <header className="mt-12 mb-10 sm:mt-8 lg:mt-4">
        <p className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.4em] sm:text-xs">
          Operator surface
        </p>
        <h1 className="mt-2 font-display text-3xl text-ash-50 tracking-wide sm:text-4xl">
          Admin
        </h1>
        <p className="mt-2 font-body text-ash-500 text-sm italic">
          Signed in as {ctx.adminEmail}
        </p>
      </header>

      <div className="lg:grid lg:grid-cols-12 lg:gap-x-10">
        <nav className="mb-8 flex gap-6 border-paper-800/60 border-b pb-3 lg:col-span-3 lg:mb-0 lg:flex-col lg:gap-2 lg:border-0 lg:pb-0">
          <AdminLink to="/admin/beats">Beats</AdminLink>
          <AdminLink to="/admin/bonds">Bonds</AdminLink>
          <AdminLink to="/admin/fallbacks">Fallbacks</AdminLink>
          <AdminLink to="/admin/memory-shards">Memory shards</AdminLink>
          <AdminLink to="/admin/cost">Cost</AdminLink>
          <AdminLink to="/admin/health">Health</AdminLink>
        </nav>
        <div className="lg:col-span-9">
          <Outlet />
        </div>
      </div>
    </main>
  );
}

function AdminLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="font-display text-[0.7rem] text-ash-400 uppercase tracking-[0.34em] transition hover:text-ash-100 sm:text-xs lg:rounded-sm lg:px-3 lg:py-2 lg:hover:bg-paper-900/40"
      activeProps={{
        className: 'text-ash-50 lg:bg-paper-900/60',
      }}
    >
      {children}
    </Link>
  );
}
